import axios from 'axios';
import { z } from 'zod';
import axiosRetry from 'axios-retry';

import {
    $CardPayment,
    $GiftCards,
    type CardPayment,
    type ProcessPaymentPayload,
    type ProcessPaymentResponse,
    Quote,
    QuoteRequest
} from '@lib/entities/Payment';

import { getAccVerificationEnabled } from '@lib/hooks/useMfaChallenge';
import { getDeviceFingerprint } from '@lib/utils/deviceFingerprint';

import client, { ApiError, handleAxiosError } from './client';
import { declareFetch } from './declarativeClient';

export interface AddPaymentV4Payload {
    cardholder_name: string;
    tracke: string;
    trackKsn: string;
    zip: string;
    storeId?: number;
    default?: boolean;
    device_fingerprint?: string;
    device_type?: string;
    platform?: string;
}

interface AddPaymentV4Response {
    valid: boolean;
    validation_results?: { error_message: string; member_names?: string[] }[];
}

const addPayment = async (
    payment: AddPaymentV4Payload
): Promise<AddPaymentV4Response | ApiError> => {
    try {
        const response = await client.post<AddPaymentV4Response>(
            'v4/checkout/add-new-card',
            payment
        );
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error) {
            if (error.response?.data?.error) {
                return error.response?.data as ApiError;
            }
            return handleAxiosError(error);
        }
        throw error;
    }
};

// Contract note: wallet-info returns gift_cards + js_credit_balance in the body REGARDLESS
// of device verification. Verified → 200 with payment_methods populated; unverified → 428
// with `payment_methods: []` and `require_device_verification: true`, but gift_cards and
// js_credit_balance still present. The client interceptor (lib/api/client.ts) passes the
// 428 body through as a normal response, so both shapes parse here and the stored-value
// fields must never be dropped on the gated path.
const $fetchWalletResponse = z.object({
    // Defaulted so a gated body that omits the array (rather than sending `[]`) still
    // parses and the gift cards / credit it carries reach the UI. Deliberate trade-off, not
    // an oversight: the schema can't distinguish a 200 from a 428, so this also loosens the
    // verified path — a malformed 200 that omits `payment_methods` now renders the empty
    // state instead of erroring. Accepted because the backend sends `[]` (never omits it) on
    // both bodies, so the default is defense-in-depth that should never fire in practice.
    payment_methods: z.array($CardPayment).optional().default([]),
    gift_cards: z.array($GiftCards).optional(),
    js_credit_balance: z.number().optional().default(0),
    gateway_legend: z.string().optional(),
    // Set on a 428 response when the user's device isn't verified — saved payment
    // methods are gated until they validate (gift cards / credit are not). The 428
    // interceptor passes the body through as a normal response, so this surfaces here
    // rather than as an error.
    require_device_verification: z.boolean().optional(),
    // v4+ (JSAD-6863): true when the user has a card stranded on the inactive
    // gateway with no equivalent on the active one (FreedomPay migration or an
    // emergency rollback). Drives the differentiated "re-add your card" empty state.
    saved_card_needs_reentry: z.boolean().optional(),
    // v4+ (JSAD-7344): how the user paid on their most recent order, so Fast Reorder can
    // pre-select it on ANY device — unlike the locally persisted selection, which is
    // per-device. `'apple_pay'` flags no card. Withheld on the untrusted-device 428 path,
    // where saved cards are gated and there is nothing to pre-select.
    //
    // Both last-used fields are HINTS and fail soft (`.catch(undefined)`): declareFetch
    // throws on any parse failure, so a new enum value from the backend (`'google_pay'`,
    // `'gift_card'`, …) or drift in the card's shape would otherwise fail the whole
    // wallet-info call and empty the wallet on every checkout. Undefined reads as "no
    // last-used method", which Fast Reorder treats as nothing it may charge.
    last_used_payment_type: z.enum(['saved_card', 'apple_pay', 'none']).optional().catch(undefined),
    // The card itself, sent WHOLE rather than as a flag on the matching `payment_methods`
    // entry. It duplicates one of those entries, so it is matched back to the list by id
    // instead of being selected directly — the list entries are what the rest of the
    // wallet (change-payment, checkout) holds identity against.
    //
    // Declared here because `z.object` STRIPS unknown keys: an undeclared field does not
    // reach the saga at all, it silently vanishes.
    last_payment_method_used: $CardPayment.optional().nullable().catch(undefined)
});
interface FetchWalletParams {
    storeId?: number;
}
// v5 additionally identifies the device for the MFA/device-verification gate.
// Sent as snake_case query params (`user_id`, `device_fingerprint`) to match the wire.
interface FetchWalletV5Params extends FetchWalletParams {
    user_id?: number;
    device_fingerprint?: string;
}
// Two declared fetchers — the version segment can't be a path param, so we
// pick the right one based on `isMfaEnabled`. `isMfaEnabled` stays out of the
// query string this way (only `storeId` is forwarded as a query param).
const fetchWalletV4 = declareFetch<typeof $fetchWalletResponse, FetchWalletParams>({
    path: 'v4/checkout/wallet-info',
    schema: $fetchWalletResponse
});
const fetchWalletV5 = declareFetch<typeof $fetchWalletResponse, FetchWalletV5Params>({
    path: 'v5/checkout/wallet-info',
    schema: $fetchWalletResponse
});
const fetchWallet = async (
    params: FetchWalletParams & { isMfaEnabled?: boolean; userId?: number } = {}
): ReturnType<typeof fetchWalletV4> => {
    const { isMfaEnabled, userId, ...fetchParams } = params;

    if (!isMfaEnabled) {
        return fetchWalletV4(fetchParams);
    }

    // v5 requires the device identity for the device-verification gate.
    // Omit keys when missing so `interpolate` doesn't serialize them as
    // `?user_id=undefined` query params. A fingerprint that fails to resolve is sent as
    // missing, which the backend gates as an untrusted device — it must never fail the wallet
    // load itself (the Zustand `fetchWallet` would otherwise be left stuck in `loading`).
    const device_fingerprint = await getDeviceFingerprint().catch(() => undefined);
    return fetchWalletV5({
        ...fetchParams,
        ...(userId != null && { user_id: userId }),
        ...(device_fingerprint && { device_fingerprint })
    });
};

const $encryptionKeyResponse = z.object({
    publicKey: z.string(),
    trackKsn: z.string()
});
const getEncryptionKey = declareFetch<typeof $encryptionKeyResponse>({
    path: 'v1/payment/encryption-key',
    schema: $encryptionKeyResponse
});

const deletePayment = async (card: CardPayment): Promise<unknown | ApiError> => {
    try {
        const response = await client.delete(`v2/checkout/delete-payment-options?id=${card.id}`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error) {
            if (error.response?.data?.error) {
                return error.response?.data as ApiError;
            }
            return handleAxiosError(error);
        }
        throw error;
    }
};

const checkout = async (
    processPayment: ProcessPaymentPayload
): Promise<ProcessPaymentResponse | ApiError> => {
    try {
        // Route to v6 when account verification is on; otherwise keep the v5 endpoint.
        const path = getAccVerificationEnabled()
            ? 'v6/checkout/process-payment'
            : 'v5/checkout/process-payment';
        const response = await client.post(path, processPayment);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error) {
            if (error.response?.data?.error) {
                return error.response?.data as ApiError;
            }
            return handleAxiosError(
                error,
                'Hmm.. this is taking longer than usual. Check your order history to make sure your order went through!'
            );
        }
        throw error;
    }
};

const getQuote = async (quote: QuoteRequest): Promise<Quote | ApiError> => {
    try {
        // Apply retry configuration to the client for this request
        axiosRetry(client, {
            retries: 3,
            retryCondition: axiosRetry.isNetworkOrIdempotentRequestError
        });

        // Route to v6 when account verification is on; otherwise keep the v3 endpoint.
        const path = getAccVerificationEnabled() ? 'v6/checkout/quote' : 'v3/checkout/quote';
        const response = await client.post(path, quote);

        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error) {
            console.error(error);
            // v6 quote returns 428 when an unverified account applies a promo code. Surface
            // the account-verification prompt instead of the raw backend error. (Only the
            // quote endpoint's 428 reaches here — the global interceptor passes every other
            // endpoint's 428 through as a success.)
            if (error.response?.status === 428) {
                // statusCode lets callers render this as a warning (yellow) toast rather than
                // the default error toast — it's a soft "verify first" nudge, not a failure.
                return {
                    error: 'To use a promo code, please first verify your account',
                    statusCode: 428
                } as ApiError;
            }
            if (error.response?.data?.error) {
                return error.response?.data as ApiError;
            }
            return handleAxiosError(error);
        }
        throw error;
    }
};

export default {
    addPayment,
    fetchWallet,
    getEncryptionKey,
    deletePayment,
    checkout,
    getQuote
};
